/**
 * Shop All hub (/collections/all)
 * Progressive enhancement over a fully server-rendered level 0.
 *
 * - Pills are real <a> links to real collection URLs. Without JS (or if any
 *   fetch fails) they navigate normally, which lands the shopper on the
 *   filterable collection page. With JS, clicks on [data-hub-pill] are
 *   intercepted: the hub section is re-fetched via the Section Rendering API
 *   in the target collection's context and swapped in place, and the URL is
 *   updated to /collections/all?view=<handle> with history.pushState.
 * - Below-the-fold rows render as skeleton shells; an IntersectionObserver
 *   fetches each row via the standalone `shop-all-hub-row` section
 *   (filename-addressed, with sort_by=best-selling) as it approaches the
 *   viewport.
 * - "Shop all" links and end cards carry no data attribute and always
 *   navigate normally.
 */
(function () {
  'use strict';

  var HANDLE_RE = /^[a-z0-9][a-z0-9\-_]*$/;
  var ROW_SECTION = 'shop-all-hub-row';

  function getRoot() {
    return document.querySelector('[data-hub-root]');
  }

  function getSectionWrapper(root) {
    // The Shopify section wrapper (stable across swaps; we replace its innerHTML).
    return root.closest('.shopify-section') || root.parentElement;
  }

  var rowObserver = null;
  var activeController = null;

  /* ---------------------------------------------------------------- rows */

  function initRows() {
    var root = getRoot();
    if (!root) return;

    if (rowObserver) rowObserver.disconnect();
    var pending = root.querySelectorAll('[data-hub-row][data-row-url]:not([data-hub-row-loaded])');
    if (!pending.length) return;

    rowObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          rowObserver.unobserve(entry.target);
          loadRow(entry.target);
        });
      },
      { rootMargin: '600px 0px' }
    );
    pending.forEach(function (el) {
      rowObserver.observe(el);
    });
  }

  function loadRow(shell, attempt) {
    attempt = attempt || 1;
    shell.setAttribute('data-hub-row-loaded', '');
    var rowUrl = shell.getAttribute('data-row-url');
    if (!rowUrl) return;

    var url = rowUrl.split('?')[0] + '?section_id=' + ROW_SECTION + '&sort_by=best-selling';

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('row fetch ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var row = doc.querySelector('.hub-row');
        if (!row) throw new Error('row markup missing');
        shell.replaceWith(row);
        var hs = row.querySelector('[data-hub-slider]');
        if (hs) {
          alignSliders();
          updateSliderButtons(hs);
          setTimeout(function () { updateSliderButtons(hs); }, 600);
        }
      })
      .catch(function () {
        if (attempt < 2 && document.contains(shell)) {
          // Transient failure: retry once before giving up on the row.
          setTimeout(function () { loadRow(shell, attempt + 1); }, 800);
          return;
        }
        // Keep the header + "Shop all" link; just drop the skeleton.
        shell.classList.add('hub-row--failed');
      });
  }

  /* ------------------------------------------------------ slider arrows */

  // The slider's first-slide inset must equal the page content edge, but the
  // theme's page-width padding varies with viewport (50px at 1920, 20px at
  // ~1600) while the CSS fallback assumes Dawn's 5rem floor. Measure the real
  // content edge from a row header and pin the inset to it.
  function alignSliders() {
    var root = getRoot();
    if (!root) return;
    var head = root.querySelector('.hub-row__head');
    if (!head) return;
    var edge = head.getBoundingClientRect().left + parseFloat(getComputedStyle(head).paddingLeft || '0');
    if (!(edge > 0)) return;
    var val = Math.round(edge) + 'px';
    root.querySelectorAll('[data-hub-slider]').forEach(function (hs) {
      hs.style.setProperty('--desktop-margin-left-first-item', val);
    });
  }

  function updateSliderButtons(hs) {
    var ul = hs.querySelector('.slider');
    var prev = hs.querySelector('[data-hub-prev]');
    var next = hs.querySelector('[data-hub-next]');
    if (!ul || !prev || !next) return;
    var maxScroll = ul.scrollWidth - ul.clientWidth;
    prev.hidden = ul.scrollLeft <= 2;
    next.hidden = ul.scrollLeft >= maxScroll - 2 || maxScroll <= 2;
  }

  function initSliders() {
    var root = getRoot();
    if (!root) return;
    alignSliders();
    root.querySelectorAll('[data-hub-slider]').forEach(updateSliderButtons);
  }

  // Image loads change scrollWidth after hydration; re-check shortly after.
  function initSlidersSoon() {
    initSliders();
    setTimeout(initSliders, 600);
  }

  // Element scroll events don't bubble; capture them at the document level.
  document.addEventListener(
    'scroll',
    function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('slider')) {
        var hs = t.closest('[data-hub-slider]');
        if (hs) updateSliderButtons(hs);
      }
    },
    true
  );

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initSliders, 150);
  });

  /* -------------------------------------------------------- level switch */

  function loadView(collectionPath, opts) {
    opts = opts || {};
    var root = getRoot();
    if (!root) return;
    var wrapper = getSectionWrapper(root);
    var sectionId = root.getAttribute('data-section-id');
    var allUrl = root.getAttribute('data-all-url') || '/collections/all';

    var path = collectionPath.split('?')[0];
    var fallbackHref = opts.fallbackHref || path;

    if (activeController) activeController.abort();
    activeController = new AbortController();

    root.setAttribute('aria-busy', 'true');

    fetch(path + '?section_id=' + encodeURIComponent(sectionId) + '&sort_by=best-selling', {
      signal: activeController.signal
    })
      .then(function (res) {
        if (!res.ok) throw new Error('hub fetch ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fetchedRoot = doc.querySelector('[data-hub-root]');
        if (!fetchedRoot) throw new Error('hub markup missing');

        var fetchedSection = doc.querySelector('.shopify-section');
        wrapper.innerHTML = fetchedSection ? fetchedSection.innerHTML : doc.body.innerHTML;

        var newRoot = getRoot();
        var view = newRoot ? newRoot.getAttribute('data-view') : 'all';
        var isAll = newRoot ? newRoot.getAttribute('data-level') === '0' : true;
        var newUrl = isAll ? allUrl : allUrl + '?browse=' + encodeURIComponent(view);

        if (opts.replace) {
          history.replaceState({ hubView: view }, '', newUrl);
        } else if (opts.push !== false) {
          history.pushState({ hubView: view }, '', newUrl);
        }

        // Feel like navigation: top of hub, focus the H1 for screen readers.
        if (opts.scroll !== false) {
          var top = wrapper.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top: Math.max(top, 0) });
        }
        var h1 = wrapper.querySelector('h1');
        if (h1) {
          h1.setAttribute('tabindex', '-1');
          h1.focus({ preventScroll: true });
        }

        initRows();
        initSlidersSoon();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Cross-template section render unavailable or any other failure:
        // fall back to normal navigation to the real (filterable) collection.
        window.location.assign(fallbackHref);
      })
      .finally(function () {
        var r = getRoot();
        if (r) r.removeAttribute('aria-busy');
      });
  }

  function collectionPathFor(handle) {
    var root = getRoot();
    var base = (root && root.getAttribute('data-collections-root')) || '/collections';
    return base.replace(/\/$/, '') + '/' + handle;
  }

  /* -------------------------------------------------------------- events */

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;

    var arrow = e.target.closest('[data-hub-prev], [data-hub-next]');
    if (arrow && getRoot() && getRoot().contains(arrow)) {
      var hs = arrow.closest('[data-hub-slider]');
      var ul = hs && hs.querySelector('.slider');
      if (ul) {
        var dir = arrow.hasAttribute('data-hub-prev') ? -1 : 1;
        ul.scrollBy({ left: dir * ul.clientWidth * 0.9, behavior: 'smooth' });
      }
      return;
    }

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var pill = e.target.closest('a[data-hub-pill]');
    if (!pill || !getRoot() || !getRoot().contains(pill)) return;

    e.preventDefault();
    var url = new URL(pill.href, window.location.origin);
    loadView(url.pathname, { fallbackHref: pill.href });
  });

  window.addEventListener('popstate', function (e) {
    var root = getRoot();
    if (!root) return;

    var view = (e.state && e.state.hubView) || new URLSearchParams(window.location.search).get('browse') || 'all';
    if (!HANDLE_RE.test(view)) view = 'all';
    if (view === root.getAttribute('data-view')) return;

    var path = view === 'all' ? root.getAttribute('data-all-url') || '/collections/all' : collectionPathFor(view);
    loadView(path, { push: false, fallbackHref: window.location.href });
  });

  /* ---------------------------------------------------------------- init */

  function init() {
    var root = getRoot();
    if (!root) return;

    var view = new URLSearchParams(window.location.search).get('browse');
    if (view && HANDLE_RE.test(view) && view !== root.getAttribute('data-view')) {
      // Deep link: /collections/all?browse=<handle>. Liquid can't read query
      // params ('view' is reserved by Shopify for alternate templates), so
      // level 0 rendered server-side; fetch the requested level.
      loadView(collectionPathFor(view), { replace: true, scroll: false, fallbackHref: collectionPathFor(view) });
    } else {
      history.replaceState({ hubView: root.getAttribute('data-view') || 'all' }, '', window.location.href);
      initRows();
      initSlidersSoon();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
